import type {
  ReportData,
  NewReportRequest,
  Ngrams,
  RenameReportPayload,
  MergeReportsPayload,
  FetchMergeableReportsParams,
  MergeableReportsResponse,
  BatchReportRequest,
} from "~/types/global";
import { REPORTS_CLIENT } from "../clients/reports.client";
import { ListItem, ListReportOutputDTO } from "root/domain/http/dto/report/list.report.output";
import { MergeableReportsOutputDTO } from "root/domain/http/dto/report/list.merge.reports.output";
import { SummaryOutputDTO } from "root/domain/http/dto/report/summary.output";
import {
  GenerateSummaryInputDTO,
  UpdateSummaryInputDTO,
  type UpdateSummaryInput
} from "root/domain/http/dto/report/summary.input";
import { REPORT_CONSTANTS, REPORT_LIST_DEFAULT_TYPE, REPORT_LIST_DEFAULT_STATUS } from "~/helpers/constants";

interface FetchReportListParams {
  hmacFilters?: string[] | undefined;
  page?: number;
  limit?: number;
  folderId?: string | null;
  reportType?: string;
  reportStatus?: string;
  order?: string;
  columnOrder?: string;
  searchScope?: string;
  favoritesOnly?: boolean;
}

export const fetchReportList = async ({
  hmacFilters,
  page = 1,
  limit = 50,
  folderId = null,
  reportType,
  reportStatus,
  order,
  columnOrder,
  searchScope,
  favoritesOnly,
}: FetchReportListParams = {}): Promise<ReadonlyArray<ListItem>> => {
  try {
    const filterParam = REPORT_CONSTANTS.report_list_params.hmac_filter;
    let url = '/get-saved-reports';
    const queryParams = [`page=${page}`, `limit=${limit}`];  // paginação

    // sort params (always sent)
    queryParams.push(`order=${order || 'desc'}`);
    queryParams.push(`column_order=${columnOrder || 'modified_at'}`);

    // filter params (only sent when non-default)
    if (reportType && reportType !== REPORT_LIST_DEFAULT_TYPE) {
      queryParams.push(`report_type=${encodeURIComponent(reportType)}`);
    }
    if (reportStatus && reportStatus !== REPORT_LIST_DEFAULT_STATUS) {
      queryParams.push(`report_status=${encodeURIComponent(reportStatus)}`);
    }

    if (folderId) {
      queryParams.push(`folder_id=${folderId}`);
    }

    // filtros de busca
    if (hmacFilters && hmacFilters.length > 0) {
      console.log("hmacFilters: ", hmacFilters);
      hmacFilters.forEach(filter => {
        queryParams.push(`${filterParam}=${encodeURIComponent(filter)}`);
      });
    }

    // search scope params (only sent when non-default)
    // 'reports' scope: search all HMAC columns (no hmac_column restriction)
    // 'folders' scope: restrict to folder_name HMAC column only
    if (searchScope === 'reports') {
      queryParams.push('search_scope=reports');
    } else if (searchScope === 'folders') {
      queryParams.push('hmac_column=folder_name');
      queryParams.push('search_scope=folders');
    }

    // favorites filter (only sent when active) — returns favorited reports and
    // folders regardless of folder location
    if (favoritesOnly) {
      queryParams.push('favorites_only=true');
    }

    // combinar params
    if (queryParams.length > 0) {
      url = `${url}?${queryParams.join('&')}`;
    }

    console.log(`Fetching reports with URL: ${url}`);
    const response = await REPORTS_CLIENT.get<ListItem[]>(url);
    const dataList = new ListReportOutputDTO(response.data);
    return dataList.getAllItems();
  } catch (error) {
    console.error("Error fetching reports list data:", error);
    throw error;
  }
};

// TODO - criar domain/http/dto/detail.reports.output.ts DetailsReportsOutputDTO
export const fetchReportById = async (id: string) => {
  try {
    const response = await REPORTS_CLIENT.get(`/get-one-report/${id}`);
    const data = await response.data;
    const result = data
    console.log("result: ", result);
    return result;
  } catch (error) {
    console.error("Error fetching report:", error);
    throw error;
  }
};

/**
 * Classificação detalhada de `redes_sociais`, espelhando o bloco `social` do
 * `DetectTypeResponse` do backend (task 3b). Preenchido só quando
 * `detected_type === "redes_sociais"`; `null` para os demais tipos.
 *
 * - `mode`: `url` (URL de rede conhecida), `identifier` (handle/@ ou ID numérico
 *   que exige escolher a rede), `invalid` (não é social).
 * - `network`/`entry_kind`: resolvidos da URL; `null` no modo identificador.
 * - `normalized_value`: URL normalizada (modo url) ou o identificador (modo id).
 */
export interface SocialDetection {
  mode: "url" | "identifier" | "invalid";
  network: string | null;
  entry_kind: string | null;
  normalized_value: string;
  identifier: string | null;
}

export interface DetectTypeResult {
  detected_type: string | null;
  is_valid: boolean;
  normalized_value: string;
  suggested_mask: string | null;
  social?: SocialDetection | null;
}

/**
 * Detecta/valida/normaliza o tipo de um Valor_Busca via a API — a API é a
 * ÚNICA fonte da Regra_Deteccao (US 21941). O frontend não mantém classificador
 * próprio: consome este resultado.
 *
 * Segurança (Req 5.3): NÃO logar o `value` nem o `normalized_value` — ambos são
 * o Valor_Busca. Em erro, logar só o tipo do erro, nunca o corpo da requisição.
 */
export const detectReportType = async (value: string): Promise<DetectTypeResult> => {
  try {
    const response = await REPORTS_CLIENT.post<DetectTypeResult>(`/detect-type`, { value });
    return response.data;
  } catch (error) {
    // Sem o valor no log — só o nome do erro.
    console.error("detectReportType error:", (error as Error)?.name ?? "unknown");
    throw error;
  }
};

export interface DetectTypeBatchResult {
  /** Um resultado por valor, na MESMA ORDEM da requisição (o backend não ecoa o valor). */
  results: DetectTypeResult[];
}

/**
 * Detecção em LOTE — uma chamada para até 50 valores, preservando a ordem.
 * Usada pelo parse do modo "múltiplos" (`handleProceedToReview`) para não fazer
 * N round-trips. Mesma fonte única da Regra_Deteccao (backend).
 *
 * Segurança (Req 5.3): NÃO logar os valores; em erro só o nome do erro.
 */
export const detectReportTypeBatch = async (
  values: string[],
): Promise<DetectTypeBatchResult> => {
  try {
    const response = await REPORTS_CLIENT.post<DetectTypeBatchResult>(
      `/detect-type-batch`,
      { values },
    );
    return response.data;
  } catch (error) {
    console.error("detectReportTypeBatch error:", (error as Error)?.name ?? "unknown");
    throw error;
  }
};

// TODO - criar domain/http/dto/create.reports.output.ts CreateReportsOutputDTO
export const createNewReport = async (payload: NewReportRequest) => {
  try {
    const response = await REPORTS_CLIENT.post(`/get-data-from-snap-api`, payload);
    if (!response?.data) {
      console.log("createNewReport response: ", response)
    }
    const data = response?.data as { id: string, credits_minimun: number };
    console.log("createNewReport data: ", data)
    return data;
  } catch (error) {
    console.error("createNewReport Error creating new report:", error);
    throw error;
  }
};

// TODO - criar domain/http/dto/add.reports.output.ts AddReportsOutputDTO
export const addNewReport = async (report: ReportData, id: string, modifiedAt?: string) => {
  try {
    // Guard de concorrência otimista (#21909): quando o chamador conhece o
    // `modified_at` que a aba carregou, envia-o no header para o backend
    // rejeitar (409) o save se o relatório já foi alterado em outra aba. Só o
    // save MANUAL de detalhes passa `modifiedAt`; os demais fluxos omitem e a
    // cerca fica inativa.
    const options = modifiedAt
      ? { headers: { "If-Unmodified-Since-Report": modifiedAt } }
      : undefined;
    const response = await REPORTS_CLIENT.put(`/insert-report/${id}`, report, options);
    return response.data;
  } catch (error) {
    console.log("addNewReport Error adding new report:", error);

    throw error;
  }
};

// TODO - adicionar endpoint real para filtro
export const getSearchedReports = async (searchTerm: Ngrams) => {
  try {
    console.log("getSearchedReports searchTerm:", searchTerm);
    const response = await REPORTS_CLIENT.post(`/get-saved-reports`, searchTerm);
    const reportList = new ListReportOutputDTO(response.data).getReports();
    return reportList;
  } catch (error) {
    console.error("Error searching reports:", error);
    throw error;
  }
};

export const deleteReport = async (id: string) => {
  try {
    const response = await REPORTS_CLIENT.delete(`/delete-report/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting report:", error);
    throw error;
  }
};

export const deleteBatch = async (batchId: string) => {
  try {
    const response = await REPORTS_CLIENT.delete(`/delete-batch/${batchId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting batch:", error);
    throw error;
  }
};

export const renameReport = async (payload: RenameReportPayload) => {
  try {
    const response = await REPORTS_CLIENT.patch(`/rename-report/${payload[REPORT_CONSTANTS.new_report.report_id]}`, payload);
    return response.data;
  } catch (error) {
    console.error("Error renaming report:", error);
    throw error;
  }
};

export const mergeReports = async (payload: MergeReportsPayload) => {
  try {
    const response = await REPORTS_CLIENT.post(`/merge-reports`, payload);
    return response.data;
  } catch (error) {
    console.error("Error merging reports:", error);
    throw error;
  }
}

export const fetchMergeableReports = async ({
  report_id,
  hmacFilters,
  page = 1,
  limit = 50,
}: FetchMergeableReportsParams): Promise<MergeableReportsOutputDTO> => {
  try {
    const filterParam = REPORT_CONSTANTS.report_list_params.hmac_filter;
    let url = `/get-mergeable-reports/${report_id}`;
    const queryParams = [`page=${page}`, `limit=${limit}`];

    // filtros de busca
    if (hmacFilters && hmacFilters.length > 0) {
      console.log("hmacFilters: ", hmacFilters);
      hmacFilters.forEach((filter: string) => {
        queryParams.push(`${filterParam}=${encodeURIComponent(filter)}`);
      });
    }

    // combinar params
    if (queryParams.length > 0) {
      url = `${url}?${queryParams.join('&')}`;
    }

    console.log(`Fetching mergeable reports with URL: ${url}`);
    const response = await REPORTS_CLIENT.get<MergeableReportsResponse>(url);
    return new MergeableReportsOutputDTO(response.data);
  } catch (error) {
    console.error("Error fetching mergeable reports:", error);
    throw error;
  }
};

export const createBatchReport = async (payload: BatchReportRequest) => {
  try {
    const response = await REPORTS_CLIENT.post(`/batch-create`, payload);
    return response.data;
  } catch (error) {
    console.error("Error creating batch report:", error);
    throw error;
  }
}

export const generateSummaryRequest = async (payload: {
  report_id: string;
  data: any;
  ai_model?: string;
  user_prompt?: string;
  report_sources_data?: any[];
  report_sources_used?: any[];
  user_wrapped_dek?: string | null;
  user_dek_iv?: string | null;
  org_wrapped_dek?: string | null;
  key_version?: number | null;
}) => {
  try {
    const inputDTO = new GenerateSummaryInputDTO(
      payload.report_id,
      payload.data,
      payload.ai_model,
      payload.user_prompt,
      payload.report_sources_data,
      payload.report_sources_used,
      {
        user_wrapped_dek: payload.user_wrapped_dek,
        user_dek_iv: payload.user_dek_iv,
        org_wrapped_dek: payload.org_wrapped_dek,
        key_version: payload.key_version,
      },
    );
    console.log("[SUMARIO] - payload enviado: ", inputDTO.toJSON())
    const response = await REPORTS_CLIENT.post("/summary", inputDTO.toJSON());
    return response.data;
  } catch (error) {
    console.error("Error generating resume:", error);
    throw error;
  }
};

export const updateSummaryRequest = async (
  reportId: string,
  summaryPayload: UpdateSummaryInput,
) => {
  try {
    const inputDTO = new UpdateSummaryInputDTO(summaryPayload);
    const response = await REPORTS_CLIENT.put(
      `/summary/${reportId}`,
      inputDTO.toJSON(),
    );
    return response.data;
  } catch (error) {
    console.error("Error updating summary:", error);
    throw error;
  }
};

export const getSummaryRequest = async (reportId: string) => {
  try {
    const response = await REPORTS_CLIENT.get(`/summary/${reportId}`);
    return new SummaryOutputDTO(response.data);
  } catch (error) {
    console.error("Error fetching summary:", error);
    throw error;
  }
};
